import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import {
  ClipboardList, Plus, Target, TrendingUp, AlertTriangle, ListChecks,
  ArrowRightCircle, CalendarClock, Pencil, Trash2, X, Save, User
} from 'lucide-react';
import { toast } from 'sonner';
import { compteRenduService } from '../services/api';

// ─── Référentiels ────────────────────────────────────────────────────────────
const TYPES_RDV = [
  { value: 'premier_entretien', label: 'Premier entretien' },
  { value: 'suivi_individuel', label: 'Suivi individuel' },
  { value: 'bilan', label: 'Bilan d\'étape' },
  { value: 'suivi_post_embauche', label: 'Suivi post-embauche' },
  { value: 'autre', label: 'Autre' },
];
const STATUTS_ACTION = [
  { value: 'a_faire', label: 'À faire', color: 'bg-slate-100 text-slate-600' },
  { value: 'en_cours', label: 'En cours', color: 'bg-blue-100 text-blue-700' },
  { value: 'fait', label: 'Fait', color: 'bg-emerald-100 text-emerald-700' },
];

const typeLabel = (v) => TYPES_RDV.find((t) => t.value === v)?.label || v;
const statutMeta = (v) => STATUTS_ACTION.find((s) => s.value === v) || STATUTS_ACTION[0];

const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return d; }
};

const emptyForm = () => ({
  date_rdv: today(),
  type_rdv: 'suivi_individuel',
  objectif: '',
  avancees: '',
  freins: [],
  actions: [],
  prochaines_etapes: '',
  date_prochain_rdv: '',
});

/**
 * Outil de compte-rendu de RDV / suivi d'un bénéficiaire.
 *
 * Props :
 *  - beneficiaireId   : id du bénéficiaire (obligatoire)
 *  - beneficiaireNom  : libellé affiché
 *  - readOnly         : true côté bénéficiaire (lecture seule)
 */
const ComptesRendusBeneficiaire = ({ beneficiaireId, beneficiaireNom = '', readOnly = false }) => {
  const [comptesRendus, setComptesRendus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!beneficiaireId) return;
    setLoading(true);
    try {
      const res = await compteRenduService.list(beneficiaireId);
      setComptesRendus(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement des comptes-rendus');
    } finally {
      setLoading(false);
    }
  }, [beneficiaireId]);

  useEffect(() => { load(); }, [load]);

  // ── Gestion du formulaire ──────────────────────────────────────────────────
  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setShowDialog(true); };
  const openEdit = (cr) => {
    setEditingId(cr.id);
    setForm({
      date_rdv: cr.date_rdv || today(),
      type_rdv: cr.type_rdv || 'suivi_individuel',
      objectif: cr.objectif || '',
      avancees: cr.avancees || '',
      freins: cr.freins || [],
      actions: cr.actions || [],
      prochaines_etapes: cr.prochaines_etapes || '',
      date_prochain_rdv: cr.date_prochain_rdv || '',
    });
    setShowDialog(true);
  };

  const addFrein = () => setForm((f) => ({ ...f, freins: [...f.freins, ''] }));
  const updateFrein = (i, v) => setForm((f) => ({ ...f, freins: f.freins.map((x, idx) => (idx === i ? v : x)) }));
  const removeFrein = (i) => setForm((f) => ({ ...f, freins: f.freins.filter((_, idx) => idx !== i) }));

  const addAction = () =>
    setForm((f) => ({ ...f, actions: [...f.actions, { libelle: '', echeance: '', responsable: '', statut: 'a_faire' }] }));
  const updateAction = (i, key, v) =>
    setForm((f) => ({ ...f, actions: f.actions.map((a, idx) => (idx === i ? { ...a, [key]: v } : a)) }));
  const removeAction = (i) => setForm((f) => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.date_rdv) { toast.error('La date du RDV est obligatoire'); return; }
    if (!form.objectif?.trim() && !form.avancees?.trim()) {
      toast.error('Renseignez au moins l\'objectif ou les avancées');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        beneficiaire_id: beneficiaireId,
        freins: form.freins.map((x) => x.trim()).filter(Boolean),
        actions: form.actions.filter((a) => a.libelle?.trim()),
        date_prochain_rdv: form.date_prochain_rdv || null,
      };
      if (editingId) {
        await compteRenduService.update(editingId, payload);
        toast.success('Compte-rendu mis à jour');
      } else {
        await compteRenduService.create(payload);
        toast.success('Compte-rendu enregistré');
      }
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cr) => {
    if (!window.confirm('Supprimer définitivement ce compte-rendu ?')) return;
    try {
      await compteRenduService.delete(cr.id);
      toast.success('Compte-rendu supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression non autorisée');
    }
  };

  // ── Rendu ───────────────────────────────────────────────────────────────────
  return (
    <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-4 px-0 pt-0">
        <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
          <ClipboardList className="w-5 h-5 text-blue-600" />
          Comptes-rendus de suivi
          <span className="text-sm font-normal text-slate-500">({comptesRendus.length})</span>
        </CardTitle>
        {!readOnly && (
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="cr-new-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau compte-rendu
          </Button>
        )}
      </CardHeader>

      <CardContent className="px-0 pb-0">
        {loading ? (
          <div className="text-center py-8 text-slate-500 text-sm">Chargement…</div>
        ) : comptesRendus.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-slate-200 rounded-2xl">
            <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm">Aucun compte-rendu pour le moment.</p>
            {!readOnly && <p className="text-slate-400 text-xs mt-1">Créez le premier pour démarrer le suivi.</p>}
          </div>
        ) : (
          <div className="space-y-4">
            {comptesRendus.map((cr) => (
              <CompteRenduCard
                key={cr.id}
                cr={cr}
                readOnly={readOnly}
                onEdit={() => openEdit(cr)}
                onDelete={() => handleDelete(cr)}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog de saisie / édition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <ClipboardList className="w-5 h-5 text-blue-600" />
              {editingId ? 'Modifier le compte-rendu' : 'Nouveau compte-rendu'}
            </DialogTitle>
            <DialogDescription>
              {beneficiaireNom ? `Suivi de ${beneficiaireNom}. ` : ''}
              Le bénéficiaire peut consulter ce compte-rendu : restez factuel et bienveillant.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-3 -mr-3">
            <div className="space-y-5 py-2">
              {/* Date & type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date du RDV *</Label>
                  <Input type="date" value={form.date_rdv}
                    onChange={(e) => setForm({ ...form, date_rdv: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Type de RDV</Label>
                  <Select value={form.type_rdv} onValueChange={(v) => setForm({ ...form, type_rdv: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES_RDV.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Objectif */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Target className="w-4 h-4 text-blue-600" />Objectif du RDV</Label>
                <Textarea value={form.objectif} onChange={(e) => setForm({ ...form, objectif: e.target.value })}
                  placeholder="Quel était l'objectif de ce rendez-vous ?" className="rounded-xl" rows={2} />
              </div>

              {/* Avancées */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" />Avancées / points abordés</Label>
                <Textarea value={form.avancees} onChange={(e) => setForm({ ...form, avancees: e.target.value })}
                  placeholder="Progrès constatés depuis le dernier RDV, points discutés…" className="rounded-xl" rows={3} />
              </div>

              {/* Freins */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Freins identifiés</Label>
                  <Button type="button" variant="outline" size="sm" className="rounded-lg h-7" onClick={addFrein}>
                    <Plus className="w-3 h-3 mr-1" />Ajouter
                  </Button>
                </div>
                {form.freins.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucun frein renseigné.</p>
                ) : form.freins.map((fr, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={fr} onChange={(e) => updateFrein(i, e.target.value)}
                      placeholder="Ex : mobilité, garde d'enfants, langue…" className="rounded-xl" />
                    <Button type="button" variant="ghost" size="sm" className="text-red-500 px-2" onClick={() => removeFrein(i)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-purple-600" />Plan d'actions</Label>
                  <Button type="button" variant="outline" size="sm" className="rounded-lg h-7" onClick={addAction}>
                    <Plus className="w-3 h-3 mr-1" />Ajouter
                  </Button>
                </div>
                {form.actions.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucune action planifiée.</p>
                ) : form.actions.map((a, i) => (
                  <div key={i} className="p-3 rounded-xl border border-slate-200 bg-slate-50 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={a.libelle} onChange={(e) => updateAction(i, 'libelle', e.target.value)}
                        placeholder="Action à réaliser" className="rounded-xl bg-white" />
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 px-2" onClick={() => removeAction(i)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input type="date" value={a.echeance || ''} onChange={(e) => updateAction(i, 'echeance', e.target.value)}
                        className="rounded-xl bg-white" />
                      <Input value={a.responsable || ''} onChange={(e) => updateAction(i, 'responsable', e.target.value)}
                        placeholder="Responsable" className="rounded-xl bg-white" />
                      <Select value={a.statut || 'a_faire'} onValueChange={(v) => updateAction(i, 'statut', v)}>
                        <SelectTrigger className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUTS_ACTION.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Prochaines étapes & prochain RDV */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><ArrowRightCircle className="w-4 h-4 text-blue-600" />Prochaines étapes</Label>
                <Textarea value={form.prochaines_etapes} onChange={(e) => setForm({ ...form, prochaines_etapes: e.target.value })}
                  placeholder="Ce qui est prévu d'ici le prochain rendez-vous…" className="rounded-xl" rows={2} />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-orange-600" />Date du prochain RDV (optionnel)</Label>
                <Input type="date" value={form.date_prochain_rdv || ''}
                  onChange={(e) => setForm({ ...form, date_prochain_rdv: e.target.value })} className="rounded-xl w-full sm:w-60" />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="pt-3">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="cr-save-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ─── Carte d'un compte-rendu ─────────────────────────────────────────────────
const CompteRenduCard = ({ cr, readOnly, onEdit, onDelete }) => {
  const prochainDepasse = cr.date_prochain_rdv && cr.date_prochain_rdv < today();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`cr-card-${cr.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-blue-100 text-blue-700 text-xs">{typeLabel(cr.type_rdv)}</Badge>
            <span className="font-bold text-slate-900">{fmtDate(cr.date_rdv)}</span>
          </div>
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
            <User className="w-3 h-3" />{cr.intervenant_prenom} {cr.intervenant_nom}
          </p>
        </div>
        {!readOnly && (
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="sm" className="px-2 text-slate-500" onClick={onEdit}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" className="px-2 text-red-500" onClick={onDelete}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      <div className="mt-3 space-y-3 text-sm">
        {cr.objectif && (
          <div>
            <p className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Target className="w-3.5 h-3.5 text-blue-600" />Objectif</p>
            <p className="text-slate-700 whitespace-pre-wrap">{cr.objectif}</p>
          </div>
        )}
        {cr.avancees && (
          <div>
            <p className="text-xs font-semibold text-slate-500 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-emerald-600" />Avancées</p>
            <p className="text-slate-700 whitespace-pre-wrap">{cr.avancees}</p>
          </div>
        )}
        {cr.freins?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" />Freins</p>
            <div className="flex flex-wrap gap-1">
              {cr.freins.map((fr, i) => <Badge key={i} className="bg-amber-50 text-amber-700 border border-amber-200 text-xs font-normal">{fr}</Badge>)}
            </div>
          </div>
        )}
        {cr.actions?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><ListChecks className="w-3.5 h-3.5 text-purple-600" />Plan d'actions</p>
            <div className="space-y-1">
              {cr.actions.map((a, i) => {
                const sm = statutMeta(a.statut);
                return (
                  <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5">
                    <span className="text-slate-700 truncate">
                      {a.libelle}
                      {a.echeance && <span className="text-slate-400 text-xs"> · {fmtDate(a.echeance)}</span>}
                      {a.responsable && <span className="text-slate-400 text-xs"> · {a.responsable}</span>}
                    </span>
                    <Badge className={`${sm.color} text-xs shrink-0`}>{sm.label}</Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {cr.prochaines_etapes && (
          <div>
            <p className="text-xs font-semibold text-slate-500 flex items-center gap-1"><ArrowRightCircle className="w-3.5 h-3.5 text-blue-600" />Prochaines étapes</p>
            <p className="text-slate-700 whitespace-pre-wrap">{cr.prochaines_etapes}</p>
          </div>
        )}
        {cr.date_prochain_rdv && (
          <div className={`flex items-center gap-1 text-xs font-medium ${prochainDepasse ? 'text-red-600' : 'text-orange-600'}`}>
            <CalendarClock className="w-3.5 h-3.5" />
            Prochain RDV : {fmtDate(cr.date_prochain_rdv)}{prochainDepasse ? ' — à reprogrammer' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComptesRendusBeneficiaire;
